import { useEffect, useMemo, useState } from "react";
import { usePublicSeo } from "@/hooks/usePublicContent";
import { isStoragePath, resolveAssetUrls } from "@/lib/mediaStorage";
import { brand } from "@/mocks/siteData";

export interface SeoStructuredData {
  [key: string]: unknown;
}

type OgType = "website" | "article" | "profile";

interface SeoProps {
  title: string;
  description: string;
  path?: string;
  keywords?: string;
  image?: string;
  structuredData?: SeoStructuredData | null | (SeoStructuredData | null)[];
  noindex?: boolean;
  /** Open Graph object type. Detail/article pages should pass "article". */
  ogType?: OgType;
  /**
   * Canonical target when it differs from `path` (rare — e.g. a paginated or
   * filtered view that should point at its parent). Ignored when the page has
   * an admin-configured canonical.
   */
  canonicalPath?: string;
  /**
   * Item-level values for the {title} / {description} placeholders used by
   * section-wide SEO entries. Detail pages pass the record's own short name and
   * summary here so a template like "{title} | Rythugaru" stays clean, while the
   * full `title` / `description` above remain the fallback when no override is set.
   */
  tokens?: { title?: string; description?: string };
}

const getPrefix = (): string => {
  const base = __BASE_PATH__.split("/").filter(Boolean).join("/");
  return base ? `/${base}` : "";
};

/**
 * The one production origin every absolute URL is built from.
 *
 * This is deliberately a constant rather than `window.location.origin`: the
 * site answers on both www.rythugaru.life and rythugaru.life (plus preview
 * hosts), and deriving canonicals from the current host makes each host
 * declare itself the original — which is exactly how www/apex duplicates get
 * indexed separately. Locking the origin here means canonical, og:url and all
 * structured data point at the apex domain no matter which host served the
 * page.
 */
const CANONICAL_ORIGIN = "https://rythugaru.life";

export const getAbsoluteUrl = (path: string): string => {
  const prefix = getPrefix();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${CANONICAL_ORIGIN}${prefix}${cleanPath}`;
};

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const DESCRIPTION_LIMIT = 160;

/**
 * Meta descriptions past ~160 characters get truncated mid-sentence in search
 * results, and a half-cut sentence reads worse than a deliberately shortened
 * one. This trims at the last complete word and drops trailing punctuation so
 * every page — including ones whose description is composed at runtime from
 * Backend content — stays inside the limit.
 */
const clampDescription = (value: string, limit = DESCRIPTION_LIMIT): string => {
  const text = value.replace(/\s+/g, " ").trim();
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  const safe = lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${safe.replace(/[\s,;:.\-–—]+$/, "")}…`;
};

/**
 * Indexable pages explicitly opt into large image previews and full snippets,
 * which is what lets a result show a big thumbnail in Discover and full-length
 * text in search. Without it Google falls back to conservative defaults.
 */
const INDEX_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const NOINDEX_ROBOTS = "noindex, nofollow";

export default function Seo({
  title,
  description,
  path = "",
  keywords,
  image,
  structuredData,
  noindex = false,
  ogType = "website",
  canonicalPath,
  tokens,
}: SeoProps) {
  const override = usePublicSeo(path);

  // Admin overrides may carry {title} / {description} tokens so a single
  // section-wide entry can still keep each item's own wording. Detail pages
  // pass the record's short name/summary via `tokens`; otherwise we fall back
  // to the full title/description.
  const tokenMap: Record<string, string> = {
    title: tokens?.title ?? title,
    description: tokens?.description ?? description,
  };
  const applyTokens = (template: string | null | undefined, fallback: string): string => {
    if (!template) return fallback;
    return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
      key in tokenMap ? tokenMap[key] : whole,
    );
  };

  const finalTitle = applyTokens(override?.title, title);
  const finalDescription = applyTokens(override?.description, description);
  const finalKeywords = override?.keywords || keywords;
  const finalImage = override?.image || image;
  const metaDescription = useMemo(() => clampDescription(finalDescription), [finalDescription]);

  // An admin-configured canonical always wins; otherwise derive it from the
  // page path so every indexable page self-references exactly one URL.
  const canonical = useMemo(() => {
    const configured = override?.canonical?.trim();
    if (configured) {
      return isAbsoluteUrl(configured) ? configured : getAbsoluteUrl(configured);
    }
    return getAbsoluteUrl(canonicalPath ?? path);
  }, [override, canonicalPath, path]);

  // Drop empty schema nodes (e.g. a FAQPage built from zero questions) so we
  // never emit invalid structured data that Google would flag.
  const jsonLd = useMemo(() => {
    if (!structuredData) return null;
    const list = (Array.isArray(structuredData) ? structuredData : [structuredData])
      .filter((entry): entry is SeoStructuredData => Boolean(entry));
    if (list.length === 0) return null;
    return list.length === 1 ? list[0] : list;
  }, [structuredData]);

  // A Media Library share image may be stored as a Backend storage path. Social
  // crawlers need an absolute URL in og:image, so resolve storage paths to a
  // signed URL at render time (external https URLs pass through untouched).
  const [resolvedImage, setResolvedImage] = useState("");
  useEffect(() => {
    let active = true;
    if (!finalImage || !isStoragePath(finalImage)) {
      setResolvedImage("");
      return () => {
        active = false;
      };
    }
    resolveAssetUrls([finalImage]).then((map) => {
      if (active) setResolvedImage(map[finalImage] || "");
    });
    return () => {
      active = false;
    };
  }, [finalImage]);

  const ogImage = finalImage && isStoragePath(finalImage) ? resolvedImage : finalImage;

  // Head tags are written in place rather than rendered as JSX. index.html ships
  // a default set of tags (the project-level share card), and React appends
  // hoisted tags instead of replacing that static block — which left every page
  // with two og:image / og:title / description tags where the *first* one was
  // always the homepage card. Updating the existing tags means each page ends up
  // with exactly one of each, carrying that page's own values, while non-JS
  // scrapers still fall back to the static card in index.html.
  useEffect(() => {
    const head = document.head;

    const setMeta = (attr: "name" | "property", key: string, content: string) => {
      const existing = head.querySelector(`meta[${attr}="${key}"]`);
      if (!content) {
        if (existing) existing.remove();
        return;
      }
      const element = (existing as HTMLMetaElement | null) || document.createElement("meta");
      if (!existing) {
        element.setAttribute(attr, key);
        head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      const existing = head.querySelector(`link[rel="${rel}"]`);
      if (!href) {
        if (existing) existing.remove();
        return;
      }
      const element = existing || document.createElement("link");
      if (!existing) {
        element.setAttribute("rel", rel);
        head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    document.title = finalTitle;

    setMeta("name", "description", metaDescription);
    setMeta("name", "keywords", finalKeywords || "");
    setMeta("name", "robots", noindex ? NOINDEX_ROBOTS : INDEX_ROBOTS);
    setLink("canonical", canonical);

    setMeta("property", "og:type", ogType);
    setMeta("property", "og:site_name", brand.fullName);
    setMeta("property", "og:locale", "en_IN");
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", metaDescription);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage || "");
    setMeta("property", "og:image:alt", ogImage ? finalTitle : "");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", metaDescription);
    setMeta("name", "twitter:image", ogImage || "");
    setMeta("name", "twitter:image:alt", ogImage ? finalTitle : "");
  }, [finalTitle, metaDescription, finalKeywords, noindex, canonical, ogType, ogImage]);

  return (
    <>
      {jsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
    </>
  );
}